import {
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  Stack,
  Divider,
  useMediaQuery,
  Tooltip,
  Button,
  IconButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Arrow from "@mui/icons-material/ArrowForwardIosTwoTone";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { useState } from "react";
import useApi from "../hooks/useApi";
import useSnack from "../hooks/useSnack";

export default function Cards({ order, onPriorityChange }) {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("lg"));
  const navigate = useNavigate();
  const { loadApi } = useApi();
  const { showSnack } = useSnack();
  const [loading, setLoading] = useState(false);
  const [isPriority, setIsPriority] = useState(order?.priority || false);

  // Verificar si el estado es "Procesando" o "En Procesando"
  const isProcessingState = order?.status === "Procesando" || order?.status === "En Procesando";

  const formatDate = (dateString) => {
    if (!dateString) return "Fecha no disponible";
    const date = new Date(dateString);
    
    // Verificar si la fecha es válida
    if (isNaN(date.getTime())) return "Fecha inválida";
    
    // Formato: DD/MM/YYYY
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      // Estados completados (verde)
      case "ENTREGADO":
      case "Entregado":
        return "#2ECC71";
      case "DOCUMENTACIÓN RECTIFICADA":
        return "#27AE60";
      case "COMPLETADO":
      case "Completado":
        return "#229954";
        
      // Estados en procesamiento normal (azul)
      case "EN TRÁNSITO":
        return "#2980B9";
      case "DISPONIBLE PARA RECOGER EN TIENDA":
        return "#1ABC9C";
      case "Awaiting SEUR shipment":
      case "Awaiting SEUR Shipment":
      case "Awaiting SEUR Label":
      case "Awaiting SEUR En Procesando":
        return "#34495E";
        
      // Estados pendientes/espera (amarillo/naranja)
      case "En Procesando":
        return "#3498DB";
      case "Pendiente":
      case "Pendiente de pago":
        return "#F1C40F";
      case "En espera":
        return "#F39C12";
      case "Borrador":
        return "#D5DBDB";
        
      // Estados con problemas (rojo/naranja fuerte)
      case "DEVOLUCIÓN EN CURSO":
        return "#E67E22";
      case "APORTAR SOLUCIÓN":
      case "INCIDENCIA":
        return "#D35400";
      case "Cancelado":
        return "#E74C3C";
      case "Reembolsado":
        return "#C0392B";
      case "Fallido":
        return "#922B21";
      
      // Por defecto (para estados no especificados)
      case "En Proceso":
        return "#3498DB";
        
      default:
        return "#95A5A6";
    }
  };

  const handleTogglePriority = async (e) => {
    e.stopPropagation(); // Evita que el evento se propague al card
    
    // Solo permitir cambios si está en estado Procesando
    if (loading || !isProcessingState) return;
    
    setLoading(true);
    try {
      await loadApi(`admin/orders/${order?.order_number}`, true, "put");
      
      setIsPriority(!isPriority);
      showSnack(`Pedido ${!isPriority ? "marcado como" : "desmarcado de"} prioridad`, "success");
      
      // Si se proporcionó una función para actualizar el estado del padre
      if (onPriorityChange) {
        onPriorityChange(order.id, !isPriority);
      }
    } catch (error) {
      showSnack("Error al actualizar la prioridad", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      sx={{
        mb: 2,
        "&:hover": {
          boxShadow: "0 0 10px 0 rgba(0,0,0,0.1)",
          transition: "0.3s",
        },
        minHeight: "310px",
        borderRadius: "10px",
        padding: "6px",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        border: isPriority ? "2px solid #f9de4b" : "1px solid #e0e0e0", 
        transition: "0.3s",

      }}
    >
      {/* Ícono de estrella en la esquina superior derecha */}
      <Tooltip title={
        !isProcessingState 
          ? "Solo se puede priorizar pedidos en estado Procesando" 
          : isPriority 
            ? "Quitar prioridad" 
            : "Marcar como prioritario"
      }>
        <span> {/* Wrapper necesario para que Tooltip funcione con elementos deshabilitados */}
          <IconButton 
            size="small" 
            onClick={handleTogglePriority}
            sx={{ 
              position: "absolute", 
              top: "5px", 
              right: "5px",
              zIndex: 2,
              opacity: isProcessingState ? 1 : 0,
            }}
            disabled={loading || !isProcessingState}
          >
            {isPriority ? (
              <StarIcon sx={{ color: '#f9de4b' }} />
            ) : (
              <StarBorderIcon />
            )}
          </IconButton>
        </span>
      </Tooltip>
      
      <CardContent sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: '100%',
        justifyContent: 'space-between',
        flex: 1
      }}>
        {/* Contenido superior */}
        <Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Orden #{order?.order_number}</Typography>
            <Chip
              label={order?.status}
              sx={{
                color: "white",
                backgroundColor: getStatusColor(order?.status),
                mr: 4, // Margen derecho para no solapar con la estrella
              }}
              size="small"
            />
          </Box>
          <Divider />
  
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1, mb: 0, alignItems: "center" }}>
            <Typography variant="subtitle2">Origen:</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <CalendarTodayIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary" fontWeight="medium">
                {formatDate(order?.createdAt)}
              </Typography>
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary" mb={1}>
            {order?.origen}
          </Typography>
  
          <Box sx={{ mt: 1 }}>
            <Typography variant="subtitle2">Productos:</Typography>
            {order.OrderItems.slice(0, 2).map((item, index) => (
              <Typography key={index} variant="body2" color="text.secondary">
                • {item?.name} (x{item?.quantity})
              </Typography>
            ))}
            {order.OrderItems.length > 2 && (
              <Tooltip
                title={
                  <>
                    {order.OrderItems.slice(2).map((item, index) => (
                      <Typography
                        key={index}
                        variant="body2"
                        color="text.secondary"
                      >
                        • {item?.name} (x{item?.quantity})
                      </Typography>
                    ))}
                  </>
                }
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    cursor: "pointer",
                  }}
                >
                  <MoreHorizIcon fontSize="small" />
                  <Typography variant="body2" color="text.secondary">
                    +{order.OrderItems.length - 2} más
                  </Typography>
                </Box>
              </Tooltip>
            )}
            <Typography variant="body2" color="text.secondary">
              Cant. total de los productos: {order?.OrderItems.length}
            </Typography>
          </Box>
        </Box>
  
        {/* Stack que estará siempre al fondo */}
        <Stack direction="row" justifyContent="space-between" mt={2} alignItems="flex-end">
          <Typography variant="subtitle1" mt={2} fontWeight={"bold"}>
            Total: {order.total_cost} €
          </Typography>
  
          <Stack direction="row" justifyContent="space-between" ml={2} gap={1}>
            <Button
              onClick={() => navigate(`/pedidos/${order?.order_number}`)}
              sx={{
                backgroundColor: "#f9de4b",
                color: "black",
                textDecoration: "none",
                padding: "5px 10px",
                borderRadius: "6px",
                display: "flex",
                alignItems: "center",
                gap: "4px",
                fontSize: "14px",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#B0951C"
                }
              }}
            >
              <Typography
                sx={{ color: "black", fontSize: "14px", fontWeight: 500 }}
              >
                {isMobile ? "Ver más" : "Ver Detalles"}
              </Typography>
              <Arrow />
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
